import { useEffect, useState } from 'react';
import ConfirmBar from '../components/ConfirmBar';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';
import { formatWhen } from '../lib/format';
import { supabase } from '../lib/supabase';

const blank = {
  title: '',
  body: '',
  title_ar: '',
  body_ar: '',
};

export default function AlertsPage() {
  const t = useT();
  const { isManager } = useAuth();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(blank);
  const [lang, setLang] = useState('en');
  const [error, setError] = useState('');
  const [sent, setSent] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  async function load() {
    const { data, error: queryError } = await supabase
      .from('push_broadcasts')
      .select('*')
      .eq('topic', 'alerts')
      .order('created_at', { ascending: false })
      .limit(40);
    if (queryError) {
      setError(queryError.message);
      return;
    }
    setRows(data || []);
  }

  useEffect(() => {
    load();
  }, []);

  async function send(event) {
    event.preventDefault();
    if (!isManager) return;
    const title = draft.title.trim();
    const body = draft.body.trim();
    if (!title || !body) {
      setError(t('alerts.needEnglish'));
      setLang('en');
      return;
    }
    if (!confirmSend) {
      setConfirmSend(true);
      return;
    }
    setSaving(true);
    setError('');
    setSent('');
    const { error: rpcError } = await supabase.rpc('send_staff_alert', {
      p_title: title,
      p_body: body,
      p_title_ar: draft.title_ar.trim() || null,
      p_body_ar: draft.body_ar.trim() || null,
    });
    setSaving(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    setDraft(blank);
    setSent(t('alerts.sentOk'));
    setConfirmSend(false);
    load();
  }

  const previewTitle = (lang === 'ar' ? draft.title_ar : draft.title).trim() || 'Title';
  const previewBody =
    (lang === 'ar' ? draft.body_ar : draft.body).trim() ||
    'The message customers will see.';

  return (
    <section className="alerts-page">
      <header className="page-head">
        <div>
          <span className="kicker">{t('alerts.kicker')}</span>
          <h1>{t('alerts.title')}</h1>
          <p className="lede">{t('alerts.lede')}</p>
        </div>
        <div className="alerts-count">
          <strong>{rows.length}</strong>
          <span>{t('alerts.sent')}</span>
        </div>
      </header>
      {error && <p className="form-error">{error}</p>}
      {sent && <p className="form-ok">{sent}</p>}

      <div className="alerts-stage">
        <article className="dispatch">
          <header className="dispatch-head">
            <span className="material-symbol">campaign</span>
            <div>
              <h2>{t('alerts.compose')}</h2>
              <p>{t('alerts.composeHint')}</p>
            </div>
          </header>
          {!isManager ? (
            <p className="empty">{t('alerts.managersOnly')}</p>
          ) : (
            <form className="dispatch-form" onSubmit={send}>
              <div className="lang-switch" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={lang === 'en'}
                  className={lang === 'en' ? 'on' : ''}
                  onClick={() => setLang('en')}
                >
                  English
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={lang === 'ar'}
                  className={lang === 'ar' ? 'on' : ''}
                  onClick={() => setLang('ar')}
                >
                  العربية
                </button>
              </div>

              {lang === 'en' ? (
                <>
                  <label>
                    <span className="field-label">
                      {t('alerts.titleLabel')}
                      <em>{draft.title.length}/80</em>
                    </span>
                    <input
                      value={draft.title}
                      onChange={(e) =>
                        setDraft((current) => ({
                          ...current,
                          title: e.target.value,
                        }))
                      }
                      maxLength={80}
                      placeholder="Workshop closed Friday"
                      required
                    />
                  </label>
                  <label>
                    <span className="field-label">
                      {t('alerts.message')}
                      <em>{draft.body.length}/500</em>
                    </span>
                    <textarea
                      rows={5}
                      value={draft.body}
                      onChange={(e) =>
                        setDraft((current) => ({
                          ...current,
                          body: e.target.value,
                        }))
                      }
                      maxLength={500}
                      placeholder="We reopen Saturday at 9:00."
                      required
                    />
                  </label>
                </>
              ) : (
                <>
                  <label>
                    <span className="field-label">
                      {t('alerts.arabicTitle')}
                      <em>{t('alerts.optional')} · {draft.title_ar.length}/80</em>
                    </span>
                    <input
                      dir="rtl"
                      value={draft.title_ar}
                      onChange={(e) =>
                        setDraft((current) => ({
                          ...current,
                          title_ar: e.target.value,
                        }))
                      }
                      maxLength={80}
                      placeholder="الورشة مغلقة يوم الجمعة"
                    />
                  </label>
                  <label>
                    <span className="field-label">
                      {t('alerts.arabicMessage')}
                      <em>optional · {draft.body_ar.length}/500</em>
                    </span>
                    <textarea
                      dir="rtl"
                      rows={5}
                      value={draft.body_ar}
                      onChange={(e) =>
                        setDraft((current) => ({
                          ...current,
                          body_ar: e.target.value,
                        }))
                      }
                      maxLength={500}
                      placeholder="نعود السبت الساعة ٩."
                    />
                  </label>
                </>
              )}

              <div className="phone-preview" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                <span>Lock screen</span>
                <strong>{previewTitle}</strong>
                <p>{previewBody}</p>
              </div>

              {confirmSend ? (
                <ConfirmBar
                  message="This goes to every customer and staff member in the app."
                  confirmLabel="Send to everyone"
                  busy={saving}
                  onCancel={() => setConfirmSend(false)}
                  onConfirm={() => send({ preventDefault() {} })}
                />
              ) : (
                <button type="submit" className="primary dispatch-send" disabled={saving}>
                  Send to everyone
                </button>
              )}
            </form>
          )}
        </article>

        <aside className="dispatch-log">
          <header className="dispatch-head">
            <span className="material-symbol">history</span>
            <div>
              <h2>Sent</h2>
              <p>Newest first.</p>
            </div>
          </header>
          {rows.length === 0 ? (
            <p className="empty">Nothing broadcast yet.</p>
          ) : (
            <ol className="alert-log">
              {rows.map((row) => (
                <li key={row.id}>
                  <time dateTime={row.created_at}>{formatWhen(row.created_at)}</time>
                  <strong>{row.title}</strong>
                  <p>{row.body}</p>
                </li>
              ))}
            </ol>
          )}
        </aside>
      </div>
    </section>
  );
}
